import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';

import {
  accessTokenFactory,
  telephonySignalRConnection,
  telephonySignalRController,
} from 'app/services/signalR';

import { SIGNAL_R_TELEPHONY_CONNECTION_URL } from 'constants/api';
import {
  TelephonySignalREventType,
  TelephonySignalRMessageType,
  UserStatus,
} from 'constants/telephony';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';

import {
  fetchVoicemailUnreadCountAction,
  setSignalRConnectedAction,
  setSignalRConnectingAction,
  setSignalRDisconnectedAction,
  setSignalRReconnectingAction,
} from 'store/system/actions';
import { TELEPHONY_ACTION_TYPE } from 'store/system/actions/telephony.actions';
import { selectUserContactStatus } from 'store/system/selectors';

// 5-minute idle threshold matching chrome.idle.queryState(300) in the Chrome extension
const IDLE_THRESHOLD_MS = 300_000;

const TelephonySignalRContainer = () => {
  const dispatch = useDispatch();
  const { getState } = useStore();
  const isInitializedRef = useRef(false);

  // Mirror the Chrome extension's ping logic: send 'Active' or 'Idle' based on
  // recent user interaction (5-minute threshold), not the full contact status.
  // If the user is ON_CALL, always report 'Active' even if the tab is idle.
  const lastInteractionRef = useRef(Date.now());

  useEffect(() => {
    const updateInteraction = () => {
      lastInteractionRef.current = Date.now();
    };

    window.addEventListener('mousemove', updateInteraction);
    window.addEventListener('keydown', updateInteraction);

    return () => {
      window.removeEventListener('mousemove', updateInteraction);
      window.removeEventListener('keydown', updateInteraction);
    };
  }, []);

  const handleRequestPing = useCallback(() => {
    const isIdle = Date.now() - lastInteractionRef.current > IDLE_THRESHOLD_MS;

    if (!isIdle) {
      telephonySignalRConnection.invoke(TelephonySignalRMessageType.PING, 'Active')?.catch(err => {
        logger.error('[Telephony SignalR] Failed to respond to RequestPing', err);
      });

      return;
    }

    // Idle but on a call — still report Active (matches Chrome extension behavior)
    const contactStatus = selectUserContactStatus(getState());

    const pingValue = contactStatus === UserStatus.ON_CALL ? 'Active' : 'Idle';

    telephonySignalRConnection.invoke(TelephonySignalRMessageType.PING, pingValue)?.catch(err => {
      logger.error('[Telephony SignalR] Failed to respond to RequestPing', err);
    });
  }, [getState]);

  const initCallback = useCallback(async () => {
    if (isInitializedRef.current) {
      return;
    }

    isInitializedRef.current = true;

    dispatch(setSignalRConnectingAction());

    try {
      telephonySignalRConnection.init(SIGNAL_R_TELEPHONY_CONNECTION_URL, accessTokenFactory);

      await telephonySignalRConnection.start();

      telephonySignalRController.init(dispatch, getState);
      telephonySignalRController.startListeners();

      telephonySignalRConnection.on(TelephonySignalREventType.REQUEST_PING, handleRequestPing);

      telephonySignalRConnection.on(TelephonySignalREventType.ON_VOICEMAIL_RECEIVED, voicemail => {
        logger.info('[Telephony SignalR] Voicemail received', voicemail);
        dispatch({ type: TELEPHONY_ACTION_TYPE.VOICEMAIL_RECEIVED, payload: voicemail });
      });

      // Fetch initial voicemail unread count
      dispatch(fetchVoicemailUnreadCountAction());

      telephonySignalRConnection.onreconnecting(() => {
        dispatch(setSignalRReconnectingAction());
      });

      telephonySignalRConnection.onreconnected(() => {
        dispatch(setSignalRConnectedAction());
      });

      telephonySignalRConnection.onclose(() => {
        dispatch(setSignalRDisconnectedAction());
      });

      dispatch(setSignalRConnectedAction());
    } catch (error) {
      logger.error('[Telephony SignalR] Failed to initialize connection', error);
      dispatch(setSignalRDisconnectedAction());
      isInitializedRef.current = false;
    }
  }, [dispatch, getState, handleRequestPing]);

  useEffectSafeStrictMode(() => {
    initCallback();

    return () => {
      telephonySignalRController.stopListeners();

      telephonySignalRConnection.off(TelephonySignalREventType.REQUEST_PING, handleRequestPing);
      telephonySignalRConnection.off(TelephonySignalREventType.ON_VOICEMAIL_RECEIVED);

      telephonySignalRConnection.stop()?.catch(err => {
        logger.error('[Telephony SignalR] Failed to stop connection', err);
      });

      telephonySignalRConnection.resetConnection();
      isInitializedRef.current = false;

      dispatch(setSignalRDisconnectedAction());
    };
  }, [initCallback, dispatch, handleRequestPing]);

  return null;
};

export default TelephonySignalRContainer;
